import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import client from '../api/client.js'
import { useAuthStore } from './auth.js'
import { useOrgStore } from './orgStore.js'
import { assertPermission, RBAC_ERROR } from '../utils/permissions.js'
import { fetchUserSubOrgsRequest } from '../services/userService.js'
import { toUser } from '../domain/model.js'

export const useUsersStore = defineStore('users', () => {
  const authStore = useAuthStore()
  const orgStore = useOrgStore()
  const users = ref([])
  const usersCacheKey = ref('')
  const userSubOrgs = ref({})
  const userSubOrgsLoaded = ref(new Set())
  const loading = ref(false)
  const error = ref('')

  const rbacDenied = () => {
    error.value = 'Access denied'
    return { success: false, code: RBAC_ERROR, error: error.value }
  }

  const usersById = computed(() => {
    const index = {}
    for (const user of users.value || []) {
      const id = Number(user?.id)
      if (Number.isInteger(id) && id > 0) {
        index[id] = user
      }
    }

    const meId = Number(authStore.user?.id)
    if (Number.isInteger(meId) && meId > 0 && !index[meId]) {
      index[meId] = toUser(authStore.user)
    }
    return index
  })

  const resolveUserIdentity = (userId, fallbackUsername = '') => {
    const normalizedId = Number(userId)
    const found = Number.isInteger(normalizedId) ? usersById.value[normalizedId] : null
    const username = String(found?.username ?? fallbackUsername ?? '').trim()
    const fullName = String(found?.full_name ?? found?.name ?? '').trim()
    const phone = String(found?.phone ?? found?.phone_number ?? '').trim()
    const displayName = fullName || username || (Number.isInteger(normalizedId) && normalizedId > 0 ? `User #${normalizedId}` : 'Unknown user')

    return {
      id: Number.isInteger(normalizedId) && normalizedId > 0 ? normalizedId : null,
      username,
      fullName,
      phone,
      displayName,
      found: Boolean(found),
    }
  }

  const currentUsersCacheKey = () => {
    const orgId = Number(orgStore.activeOrgId)
    const subOrgId = Number(orgStore.activeSubOrgId)
    return [
      Number.isInteger(orgId) && orgId > 0 ? `org:${orgId}` : 'org:none',
      Number.isInteger(subOrgId) && subOrgId > 0 ? `sub:${subOrgId}` : 'sub:all',
    ].join('|')
  }

  const fetchUsers = async () => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org' })
      const subOrgId = Number(orgStore.activeSubOrgId)
      const hasSubOrgId = Number.isInteger(subOrgId) && subOrgId > 0

      if (!hasSubOrgId) {
        users.value = []
        usersCacheKey.value = currentUsersCacheKey()
        return { success: true, users: [] }
      }

      const requestConfig = hasSubOrgId
        ? { params: { sub_org_id: subOrgId } }
        : undefined

      const response = await client.get('/users/', requestConfig)
      const rawUsers = Array.isArray(response.data) ? response.data : []
      users.value = rawUsers.map((user) => toUser(user))
      usersCacheKey.value = currentUsersCacheKey()
      return { success: true, users: users.value }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDenied()
      }
      console.error('Failed to fetch users', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch users'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const ensureUsersHydrated = async ({ force = false } = {}) => {
    const key = currentUsersCacheKey()
    if (!force && users.value.length > 0 && usersCacheKey.value === key) {
      return { success: true, users: users.value, cached: true }
    }

    const subOrgId = Number(orgStore.activeSubOrgId)
    const hasSubOrgId = Number.isInteger(subOrgId) && subOrgId > 0
    if (hasSubOrgId) {
      return fetchUsers()
    }

    loading.value = true
    error.value = ''
    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org' })
      const response = await client.get('/users/')
      const rawUsers = Array.isArray(response.data) ? response.data : []
      users.value = rawUsers.map((user) => toUser(user))
      usersCacheKey.value = key
      return { success: true, users: users.value }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        const meUser = authStore.user ? [toUser(authStore.user)] : []
        users.value = meUser
        usersCacheKey.value = key
        return rbacDenied()
      }
      console.error('Failed to hydrate users', err)
      error.value = err?.response?.data?.detail ?? 'Failed to hydrate users'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const createUser = async (payload) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org' })
      const organizationId = Number(payload?.organization_id)
      if (!Number.isInteger(organizationId) || organizationId <= 0) {
        throw new Error('Organization must be selected')
      }
      const subOrgId = Number(payload?.sub_org_id)
      if (!Number.isInteger(subOrgId) || subOrgId <= 0) {
        throw new Error('Sub-organization is required')
      }

      const requestPayload = {
        ...payload,
        organization_id: organizationId,
        sub_org_id: subOrgId,
      }

      console.log('[CREATE USER PAYLOAD]', {
        username: requestPayload.username,
        role: requestPayload.role,
        organization_id: requestPayload.organization_id,
        sub_org_id: requestPayload.sub_org_id,
      })

      const response = await client.post('/users/', requestPayload)
      const created = response?.data
      if (created?.id) {
        users.value = [toUser(created), ...users.value]
      }
      return { success: true, user: toUser(created) }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDenied()
      }
      console.error('Failed to create user', err)
      error.value = err?.response?.data?.detail ?? err?.message ?? 'Failed to create user'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteUser = async (userId) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org' })
      const normalizedUserId = Number(userId)
      if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) {
        throw new Error('User id must be valid')
      }

      await client.delete(`/users/${normalizedUserId}`)
      users.value = users.value.filter((user) => Number(user?.id) !== normalizedUserId)

      const nextUserSubOrgs = { ...userSubOrgs.value }
      delete nextUserSubOrgs[normalizedUserId]
      userSubOrgs.value = nextUserSubOrgs

      const nextLoaded = new Set(userSubOrgsLoaded.value)
      nextLoaded.delete(normalizedUserId)
      userSubOrgsLoaded.value = nextLoaded

      return { success: true }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDenied()
      }
      console.error('Failed to delete user', err)
      error.value = err?.response?.data?.detail ?? err?.message ?? 'Failed to delete user'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateUserRole = async (userId, role) => {
    try {
      await client.patch(`/users/${userId}/role`, { role })

      const user = users.value.find((u) => u.id === userId)
      if (user) {
        user.role = role
      }

      return { success: true }
    } catch (err) {
      console.error('Failed to update user role', err)
      return { success: false, error: err }
    }
  }

  const loadUserSubOrgs = async (userId, force = false) => {
    const normalizedUserId = Number(userId)
    if (!force && userSubOrgsLoaded.value.has(normalizedUserId)) {
      return { success: true, cached: true, data: userSubOrgs.value[normalizedUserId] ?? [] }
    }

    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org' })
      if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) {
        throw new Error('User id must be valid')
      }

      const data = await fetchUserSubOrgsRequest(normalizedUserId)
      userSubOrgs.value = {
        ...userSubOrgs.value,
        [normalizedUserId]: Array.isArray(data) ? data : [],
      }
      userSubOrgsLoaded.value.add(normalizedUserId)
      return { success: true, data: userSubOrgs.value[normalizedUserId] }
    } catch (err) {
      const normalizedUserId = Number(userId)
      const status = err?.response?.status
      if (status === 403 && Number.isInteger(normalizedUserId) && normalizedUserId > 0) {
        userSubOrgs.value = {
          ...userSubOrgs.value,
          [normalizedUserId]: [],
        }
        console.warn('Not allowed to view suborgs for this user', { userId: normalizedUserId })
        return { success: true, data: [], forbidden: true }
      }

      if (err?.code === RBAC_ERROR) {
        return rbacDenied()
      }
      error.value = err?.response?.data?.detail ?? err?.message ?? 'Failed to load user sub-organizations'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const getUserSubOrgs = (userId) => {
    const normalizedUserId = Number(userId)
    if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) return []
    return userSubOrgs.value[normalizedUserId] || []
  }

  const clearUserSubOrgsCache = () => {
    userSubOrgs.value = {}
    userSubOrgsLoaded.value = new Set()
  }

  watch(
    () => orgStore.activeOrgId,
    (nextOrgId, previousOrgId) => {
      if (nextOrgId === previousOrgId) return
      clearUserSubOrgsCache()
      users.value = []
      usersCacheKey.value = ''
    },
  )

  return {
    users,
    usersById,
    userSubOrgs,
    loading,
    error,
    fetchUsers,
    ensureUsersHydrated,
    resolveUserIdentity,
    createUser,
    deleteUser,
    updateUserRole,
    loadUserSubOrgs,
    getUserSubOrgs,
    clearUserSubOrgsCache,
  }
})
